/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useMemo } from 'react';
import ThemedButton from '@/components/common/buttons/Button';
import { useAppSelector } from '@/hooks/useRedux';
import { asset } from '@/utils/runtimeOrg';
import { useAuthReady } from '@/utils/authReady';

type HeroProps = {
  authHint?: boolean;
  cfg: {
    heroSection?: { image?: string; title?: string; description?: string };
  };
};

export default function Hero({ cfg, authHint = false }: HeroProps) {
  const isLoggedIn = useAppSelector((s: any) =>
    Boolean(s?.auth?.signin?.token?.access)
  );

  const authReady = useAuthReady();
  const showAuthSkeleton = !isLoggedIn && authHint && !authReady;

  const hero = cfg?.heroSection;

  const bgStyle = useMemo(() => {
    if (!hero?.image) return undefined;
    return { backgroundImage: `url('${asset(hero.image)}')` };
  }, [hero?.image]);

  if (!hero) {
    return (
      <section className="h-[50vh] md:h-[80vh] bg-[var(--color-background)]" />
    );
  }

  const { title, description } = hero;

  return (
    <section
      id="home"
      aria-label="Welcome"
      className="
        relative
        min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh]
        py-12 sm:py-16 md:py-20
        bg-cover bg-center bg-no-repeat
        flex items-center
        overflow-hidden
      "
      style={bgStyle}
    >
      <div className="absolute inset-0 md:hidden bg-gradient-to-b from-black/55 via-black/40 to-black/25" />

      <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-black/65 via-black/35 to-transparent" />

      <div className="relative z-10 w-full">
        <div className="container mx-auto px-4 flex items-center">
          <div className="w-full max-w-[36rem] lg:max-w-[42rem]">
            <h1
              className="
                capitalize tracking-[-0.015em] font-semibold
                text-white drop-shadow-sm
                text-[clamp(1.85rem,5vw,3rem)]
                leading-[1.12]
                mb-3 sm:mb-4
              "
              style={{
                fontFamily: 'Inter, var(--font-inter), AGTextXLRegular',
              }}
            >
              {title}
            </h1>
            <p
              className="
                text-white/90 drop-shadow-sm
                text-[clamp(1rem,2.2vw,1.25rem)]
                leading-relaxed
                max-w-[34rem]
                mb-6 sm:mb-8
              "
              style={{
                fontFamily: 'Inter, var(--font-inter), AGTextXLRegular',
              }}
            >
              {description}
            </p>

            <div className="flex gap-3 sm:gap-4 flex-col sm:flex-row sm:flex-wrap">
              {showAuthSkeleton ? (
                <div
                  role="status"
                  aria-busy="true"
                  aria-label="Loading your account"
                  className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:gap-4"
                >
                  <span className="sr-only">Loading your account</span>
                  <div className="h-[34px] w-full animate-pulse rounded-lg border border-white/60 bg-black/25 backdrop-blur-[2px] sm:h-[42px] sm:w-44" />
                  <div className="h-[34px] w-full animate-pulse rounded-lg bg-white/30 sm:h-[42px] sm:w-44" />
                </div>
              ) : isLoggedIn ? (
                <>
                  <ThemedButton
                    variant="outlined"
                    onImage
                    loadingOnNavigate
                    className="p-4! sm:p-5! text-base! sm:text-lg! w-full! sm:w-44!"
                    text="My Account"
                    href="/account"
                  />
                  <ThemedButton
                    theme="primary"
                    className="p-4! sm:p-5! text-base! sm:text-lg! w-full! sm:w-44!"
                    text="Explore Services"
                    href="/#services"
                  />
                </>
              ) : (
                <>
                  <ThemedButton
                    variant="outlined"
                    onImage
                    className="p-4! sm:p-5! text-base! sm:text-lg! w-full! sm:w-44!"
                    text="Log In"
                    loadingOnNavigate
                    href="/account/signin"
                  />
                  <ThemedButton
                    theme="primary"
                    className="p-4! sm:p-5! text-base! sm:text-lg! w-full! sm:w-44!"
                    text="Create Account"
                    loadingOnNavigate
                    href="/account/signup"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
